import { CommonModule } from '@angular/common';
import {
	HTTP_INTERCEPTORS,
	HttpClient,
	HttpClientModule,
} from '@angular/common/http';
import {
	APP_INITIALIZER,
	ApplicationConfig,
	importProvidersFrom,
	inject,
	InjectionToken,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
	provideRouter,
	withPreloading,
	withViewTransitions,
} from '@angular/router';
import { AppAdapter } from '@core/adapters/app.adapter';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { SelectivePreloadingStrategy } from '@core/selective-preloading-strategy';
import {
	AppEffects,
	CORE,
	coreReducer,
	getDetailsForConnectedUser,
	IAppRootState,
} from '@core/store';
import {
	CacheInterceptor,
	DcBaseInitializationService,
} from '@datachain/ui-sdk/common';
import { AppIcons, AppModalService } from '@datachain/ui-sdk/components';
import { provideEffects } from '@ngrx/effects';
import { ActionReducerMap, provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { IProfileModel } from '@requesters/models/profile.model';
import { AppActionsIcons } from '@ui/app-actions.icons';
import { AppUiComponentIcons } from '@ui/app-components.icons';
import { AppConsoleIcons } from '@ui/app-console.icons';
import { firstValueFrom } from 'rxjs';

import { parentRoute } from './app.routes';
import { environment } from '../environments/environment';

export const CsIconsInjectable = new InjectionToken(
	'dc-console default namespace',
	{
		factory: (): AppIcons =>
			new AppConsoleIcons(inject(MatIconRegistry), inject(DomSanitizer)),
	},
);

export const ActionIconsInjectable = new InjectionToken(
	'Actions data namespace',
	{
		factory: (): AppIcons =>
			new AppActionsIcons(inject(MatIconRegistry), inject(DomSanitizer)),
	},
);

export const UiComponentIconsInjectable = new InjectionToken(
	'ADOBIS UI components data namespace',
	{
		factory: (): AppIcons =>
			new AppUiComponentIcons(inject(MatIconRegistry), inject(DomSanitizer)),
	},
);

export const APP_INIT =
	(
		http: HttpClient,
		appAdapter: AppAdapter,
		store: Store,
	): (() => Promise<unknown>) =>
	async () => {
		const isAuthenticated = await firstValueFrom(
			http.get('authenticated', {
				responseType: 'text' as const,
			}),
		);
		const paths = await firstValueFrom(
			http.get<Record<string, string>>('/private-dcc-api/apiPaths'),
		);
		DcBaseInitializationService.updateConfig(
			DcBaseInitializationService.getConfigInstance().setAvailableResources(
				paths,
			),
		);

		if (isAuthenticated) {
			const userAccount = await firstValueFrom(
				http.get<IProfileModel>('/account'),
			);

			const connectedUser = appAdapter.generateCurrentUser(userAccount);

			store.dispatch(
				getDetailsForConnectedUser({
					user: connectedUser,
				}),
			);
		}
	};

const APP_INTERCEPTORS = [
	{
		provide: HTTP_INTERCEPTORS,
		useClass: AuthInterceptor,
		multi: true,
	},
	{
		provide: HTTP_INTERCEPTORS,
		useClass: CacheInterceptor,
		multi: true,
	},
];

const rootReducer: ActionReducerMap<IAppRootState> = {
	[CORE]: coreReducer,
};

export const appConfig: ApplicationConfig = {
	providers: [
		importProvidersFrom(
			CommonModule,
			BrowserAnimationsModule,
			BrowserModule,
			HttpClientModule,
		),
		{
			provide: APP_INITIALIZER,
			useFactory: APP_INIT,
			deps: [HttpClient, AppAdapter, Store],
			multi: true,
		},
		AppModalService,
		...APP_INTERCEPTORS,
		provideRouter(
			parentRoute,
			withViewTransitions(),
			withPreloading(SelectivePreloadingStrategy),
		),
		provideStore(rootReducer),
		provideEffects(AppEffects),
		provideStoreDevtools({
			maxAge: 25,
			logOnly: environment.production,
			autoPause: true,
			trace: environment.enableDebug,
			traceLimit: 75,
			connectInZone: true,
		}),
	],
};
